import React, { useEffect, useRef, useState } from "react"
import Chevron from "../Chevron"
import { motion, AnimatePresence } from "framer-motion"
import useFixedDropdown from "../../hooks/useFixedDropdown"
import { useTranslation } from "react-i18next"

const PAGE_SIZE = 50

const SingleSelect = ({ id, openId, setOpenId, label, options, value, onChange }) => {
  const { t } = useTranslation()
  const open = openId === id
  const { triggerRef, dropRef, pos } = useFixedDropdown(open, setOpenId)
  const sel = options.find((o) => o.value === value)

  const [search, setSearch] = useState("")
  const [limit, setLimit] = useState(PAGE_SIZE)
  const searchRef = useRef(null)

  useEffect(() => {
    if (open) {
      setSearch("")
      setLimit(PAGE_SIZE)

      const timeout = setTimeout(() => {
        searchRef.current?.focus()
      }, 30)

      return () => clearTimeout(timeout)
    }
  }, [open])

  const filtered = search.trim()
    ? options.filter((o) => o.label?.toLowerCase().includes(search.trim().toLowerCase()))
    : options

  const visible = filtered.slice(0, limit)
  const hasMore = filtered.length > visible.length

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        className={`w-full flex items-center justify-between gap-1 px-2.5 py-2 text-xs bg-white border rounded-xl transition-all focus:outline-none min-w-0 ${
          open ? "border-blue-400 ring-2 ring-blue-500/20" : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <span
          className={`truncate text-left min-w-0 ${sel?.value ? "text-gray-800" : "text-gray-400"}`}
        >
          {sel ? sel.label : label}
        </span>

        <span className="flex-shrink-0">
          <Chevron open={open} />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={dropRef}
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.1, ease: "easeOut" }}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: Math.max(pos.width, 220),
              zIndex: 9999,
            }}
            className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Поиск */}
            <div className="p-1.5 border-b border-gray-100 flex-shrink-0">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setLimit(PAGE_SIZE)
                }}
                placeholder={t("admin.search")}
                className="w-full px-2.5 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>

            {/* Список */}
            <div className="max-h-60 overflow-y-auto overscroll-contain">
              {visible.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value)
                    setOpenId(null)
                  }}
                  className={`w-full flex items-center px-3 py-2.5 hover:bg-gray-50 text-left text-xs transition-colors ${
                    value === opt.value
                      ? "text-blue-600 font-semibold bg-blue-50/60"
                      : "text-gray-700"
                  }`}
                >
                  <span className="truncate min-w-0">{opt.label}</span>
                </button>
              ))}

              {filtered.length === 0 && (
                <div className="px-3 py-4 text-xs text-gray-400 text-center">
                  {t("admin.no_options")}
                </div>
              )}

              {hasMore && (
                <button
                  type="button"
                  onClick={() => setLimit((l) => l + PAGE_SIZE)}
                  className="w-full px-3 py-2 text-xs text-blue-600 hover:bg-blue-50/60 transition-colors border-t border-gray-100"
                >
                  {t("admin.show_more_with_count", {
                    count: filtered.length - visible.length,
                  })}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default SingleSelect
